import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ShieldAlert, ShieldCheck } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@flexibilitypay/ui';
import { formatDateTime } from '@flexibilitypay/shared';

import { useAccountComplianceMutations } from '../../hooks/use-account-compliance';
import {
  ComplianceActionDialog,
  resolveComplianceErrorMessage,
  type ComplianceDialogMode,
} from './compliance-action-dialog';

export type ComplianceStatus = 'CLEAR' | 'BLOCKED';

/**
 * (Backfill automático — agregar descripción)
 *
 * @taskId T060
 * @see functional/Specs/specs_cycle-2.md HU-11-FE
 */
export interface ComplianceActionsPanelProps {
  accountId: string;
  complianceStatus?: ComplianceStatus;
  blockedReason?: string;
  blockedAt?: string;
  blockedBy?: string;
  isLoading?: boolean;
}

function StatusClearBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700">
      <ShieldCheck className="size-3.5" aria-hidden />
      {label}
    </span>
  );
}

function StatusBlockedBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive">
      <ShieldAlert className="size-3.5" aria-hidden />
      {label}
    </span>
  );
}

export function ComplianceActionsPanel({
  accountId,
  complianceStatus,
  blockedReason,
  blockedAt,
  blockedBy,
  isLoading = false,
}: ComplianceActionsPanelProps) {
  const { t } = useTranslation(['compliance']);
  const [dialogMode, setDialogMode] = useState<ComplianceDialogMode | null>(null);
  const [successKey, setSuccessKey] = useState<'blocked' | 'unblocked' | null>(null);
  const [optimisticStatus, setOptimisticStatus] = useState<ComplianceStatus | undefined>(undefined);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { block, unblock } = useAccountComplianceMutations(accountId);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const activeMutation = dialogMode === 'block' ? block : unblock;
  const submitting = activeMutation.isPending;
  const errorMessage = activeMutation.isError
    ? resolveComplianceErrorMessage(activeMutation.error, t)
    : null;

  function handleOpen(mode: ComplianceDialogMode) {
    block.reset();
    unblock.reset();
    setDialogMode(mode);
  }

  function handleClose() {
    if (submitting) return;
    setDialogMode(null);
    block.reset();
    unblock.reset();
  }

  async function handleSubmit(reason: string) {
    if (!dialogMode) return;
    const mutation = dialogMode === 'block' ? block : unblock;
    const completedMode = dialogMode;
    try {
      await mutation.executeAsync({ reason });
      setDialogMode(null);
      const nextKey = completedMode === 'block' ? 'blocked' : 'unblocked';
      setSuccessKey(nextKey);
      setOptimisticStatus(completedMode === 'block' ? 'BLOCKED' : 'CLEAR');
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => setSuccessKey(null), 4000);
    } catch {
      // Error queda en mutation.error (mostrado vía resolveComplianceErrorMessage).
    }
  }

  const effectiveStatus = optimisticStatus ?? complianceStatus;
  const isBlocked = effectiveStatus === 'BLOCKED';
  const isUnknown = effectiveStatus === undefined;

  return (
    <Card data-testid="compliance-actions-panel">
      <CardHeader>
        <CardTitle className="text-base">{t('compliance:title')}</CardTitle>
        <CardDescription>
          {isBlocked
            ? t('compliance:status.blocked.description')
            : t('compliance:status.clear.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {successKey && (
          <div
            data-testid="compliance-success-toast"
            role="status"
            aria-live="polite"
            className="flex items-center gap-2 rounded-md border border-emerald-700 bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-md ring-1 ring-emerald-500/40 transition-opacity"
          >
            <CheckCircle2 className="size-4 text-white" aria-hidden />
            <span>{t(`compliance:success.${successKey}`)}</span>
          </div>
        )}
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <>
            {!isUnknown && (
              <div className="flex flex-wrap items-center gap-3">
                {isBlocked ? (
                  <StatusBlockedBadge label={t('compliance:status.blocked.label')} />
                ) : (
                  <StatusClearBadge label={t('compliance:status.clear.label')} />
                )}
              </div>
            )}
            {isUnknown && (
              <p className="text-sm text-muted-foreground">
                {t('compliance:status.unknown.disclaimer')}
              </p>
            )}
            {isBlocked && (blockedReason || blockedAt || blockedBy) && (
              <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                {blockedReason && (
                  <div className="sm:col-span-2">
                    <dt className="text-muted-foreground">
                      {t('compliance:status.blocked.reasonLabel')}
                    </dt>
                    <dd className="font-medium">{blockedReason}</dd>
                  </div>
                )}
                {blockedBy && (
                  <div>
                    <dt className="text-muted-foreground">
                      {t('compliance:status.blocked.byLabel')}
                    </dt>
                    <dd className="font-medium">{blockedBy}</dd>
                  </div>
                )}
                {blockedAt && (
                  <div>
                    <dt className="text-muted-foreground">
                      {t('compliance:status.blocked.atLabel')}
                    </dt>
                    <dd className="font-medium">{formatDateTime(blockedAt)}</dd>
                  </div>
                )}
              </dl>
            )}
            <div className="flex flex-wrap gap-2 pt-2">
              {isUnknown ? (
                <>
                  <Button variant="destructive" onClick={() => handleOpen('block')}>
                    {t('compliance:actions.block')}
                  </Button>
                  <Button variant="outline" onClick={() => handleOpen('unblock')}>
                    {t('compliance:actions.unblock')}
                  </Button>
                </>
              ) : isBlocked ? (
                <Button onClick={() => handleOpen('unblock')}>
                  {t('compliance:actions.unblock')}
                </Button>
              ) : (
                <Button variant="destructive" onClick={() => handleOpen('block')}>
                  {t('compliance:actions.block')}
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
      {dialogMode && (
        <ComplianceActionDialog
          open
          mode={dialogMode}
          submitting={submitting}
          errorMessage={errorMessage}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      )}
    </Card>
  );
}
